import Link from "next/link";
import { useTranslation } from "react-i18next";

import LegalPageLayout from "@/my_components/Common/LegalPageLayout";
import CallToActionOne from "@/my_components/CallToAction/CallToActionOne";
import Newsletter from "@/my_components/Newsletters/Newsletter";
import { ROUTE } from "@/route/app_routes.js";
import { CONFIG } from "@/app_config.js";
import { image_url } from "@/helper/Utilities";

const LAST_UPDATED = "September 2026";

const CookieTable = ({ t }) => (
  <div className="rbt-legal-table-wrap">
    <table className="rbt-legal-table">
      <thead>
        <tr>
          <th>{t("privacy.cookieTable.colCookie", "Cookie / storage item")}</th>
          <th>{t("privacy.cookieTable.colType", "Type")}</th>
          <th>{t("privacy.cookieTable.colPurpose", "Purpose")}</th>
          <th>{t("privacy.cookieTable.colDuration", "Duration")}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>{t("privacy.cookieTable.row1.name", "Authentication cookie")}</strong></td>
          <td>{t("privacy.cookieTable.row1.type", "Strictly necessary")}</td>
          <td>{t("privacy.cookieTable.row1.purpose", "Keeps you signed in. It is httpOnly, so it cannot be read by JavaScript, and it is never used for advertising or cross-site tracking.")}</td>
          <td>{t("privacy.cookieTable.row1.duration", "Session / until logout")}</td>
        </tr>
        <tr>
          <td><strong>{t("privacy.cookieTable.row2.name", "Cookie-consent flag")}</strong></td>
          <td>{t("privacy.cookieTable.row2.type", "Strictly necessary")}</td>
          <td>{t("privacy.cookieTable.row2.purpose", "A small local-storage entry that remembers you have acknowledged our cookie notice, so it isn't shown on every visit.")}</td>
          <td>{t("privacy.cookieTable.row2.duration", "Until cleared by you")}</td>
        </tr>
        <tr>
          <td><strong>{t("privacy.cookieTable.row3.name", "Preference storage")}</strong></td>
          <td>{t("privacy.cookieTable.row3.type", "Functional")}</td>
          <td>{t("privacy.cookieTable.row3.purpose", "Remembers display settings such as your theme (light/dark/gray/system) and language, so the site looks and reads right the next time you visit.")}</td>
          <td>{t("privacy.cookieTable.row3.duration", "Until cleared by you")}</td>
        </tr>
        <tr>
          <td><strong>{t("privacy.cookieTable.row4.name", "Analytics identifiers")}</strong></td>
          <td>{t("privacy.cookieTable.row4.type", "Analytics")}</td>
          <td>{t("privacy.cookieTable.row4.purpose", "Helps us understand aggregate usage (e.g., which courses are popular, where errors occur) so we can improve the Service. Not used to build advertising profiles.")}</td>
          <td>{t("privacy.cookieTable.row4.duration", "Up to 24 months")}</td>
        </tr>
        <tr>
          <td><strong>{t("privacy.cookieTable.row5.name", "YouTube embedded-video cookies")}</strong></td>
          <td>{t("privacy.cookieTable.row5.type", "Third-party (set by YouTube/Google, not by us)")}</td>
          <td>{t("privacy.cookieTable.row5.purpose", "Set directly by YouTube's player when you play a lesson's embedded video — for example, to run the player, measure viewership, or serve YouTube's own recommendations. See “Embedded course videos” below.")}</td>
          <td>{t("privacy.cookieTable.row5.duration", "Set and controlled by Google — see YouTube's own policies")}</td>
        </tr>
      </tbody>
    </table>
  </div>
);

const useSections = (t) => {
  const appName = CONFIG.APP_NAME;

  return [
    {
      id: "introduction",
      navLabel: t("privacy.sections.introduction.navLabel", "Introduction & Scope"),
      title: t("privacy.sections.introduction.title", "1. Introduction and Scope"),
      body: (
        <>
          <p>
            {t(
              "privacy.sections.introduction.para1",
              "{{appName}} (“we,” “us,” or “our”) respects your privacy. This Privacy Policy explains what personal data we collect when you use our website, courses, certificates, community features, and events (the “Service”), why we collect it, how we use and share it, and the rights you have over it.",
              { appName }
            )}
          </p>
          <p>
            {t(
              "privacy.sections.introduction.para2",
              "This Policy applies to all users of the Service worldwide. We aim to meet the standards of major privacy frameworks, including the EU/UK General Data Protection Regulation (“GDPR”) and the California Consumer Privacy Act (“CCPA”), and we extend the core rights described in Section 9 to users everywhere, regardless of where you live."
            )}
          </p>
        </>
      ),
    },
    {
      id: "information-we-collect",
      navLabel: t("privacy.sections.infoWeCollect.navLabel", "Information We Collect"),
      title: t("privacy.sections.infoWeCollect.title", "2. Information We Collect"),
      body: (
        <>
          <p><strong>{t("privacy.sections.infoWeCollect.subheading1", "Account information you provide:")}</strong></p>
          <ul>
            <li>{t("privacy.sections.infoWeCollect.accountListItem1", "Name, email address, and optional details such as gender, age range, and country provided at registration.")}</li>
            <li>{t("privacy.sections.infoWeCollect.accountListItem2", "Your password, stored using industry-standard one-way hashing — we never store or have access to your password in plain text.")}</li>
            <li>{t("privacy.sections.infoWeCollect.accountListItem3", "Optional profile details you choose to add: a profile photo (which you can crop, rotate, and adjust before it is stored), a dashboard banner image or pattern, short bio, profession, and links to your social media profiles.")}</li>
          </ul>
          <p><strong>{t("privacy.sections.infoWeCollect.subheading2", "Information generated as you use the Service:")}</strong></p>
          <ul>
            <li>{t("privacy.sections.infoWeCollect.usageListItem1", "Course enrollments, lesson progress, quiz attempts and scores, and certificates earned. Some quizzes are time-limited; if time runs out, your quiz is automatically submitted with the answers you had entered.")}</li>
            <li>{t("privacy.sections.infoWeCollect.usageListItem2", "Private study notes you attach to a lesson, visible only to you by default (see Section 6 for the limited circumstances in which this can change), and any course reviews or star ratings you choose to post, which are public and attributed to your name.")}</li>
            <li>{t("privacy.sections.infoWeCollect.usageListItem3", "Wishlist items, event registrations, ticket purchases, and any assignments or forum posts you submit. Reserving an event spot collects the attendee's name, email, and phone number — which may belong to you or, if you are booking on someone else's behalf, to that other person. If the email provided is not already a verified account email, we send a short-lived 6-digit verification code to it (see Section 6 for who an event reservation is shared with, and the item below for how the code itself works) before the reservation is confirmed, so that we have reasonable assurance the reservation is being made with that email address's genuine cooperation.")}</li>
            <li>{t("privacy.sections.infoWeCollect.usageListItem4", "Basic technical and device data, such as IP address, browser type, device type, and approximate location derived from IP address, used for security (for example, detecting simultaneous logins from an unusual number of devices) and troubleshooting.")}</li>
            <li>{t("privacy.sections.infoWeCollect.usageListItem5", "Short-lived 6-digit verification codes and their expiry time, generated fresh and automatically invalidated once used or expired: for optional two-factor authentication at login (see Section 7), and for confirming an event reservation made with an email that is not already a verified account email.")}</li>
            <li>{t("privacy.sections.infoWeCollect.usageListItem6", "Your display and platform preferences — such as theme (light, dark, gray, or system), dashboard banner style, language, and notification settings — so the Service looks and behaves the way you last set it up, including when you sign in from a different device.")}</li>
          </ul>
          <p>
            <strong>{t("privacy.sections.infoWeCollect.cookiesNote", "Cookies and similar technologies:")}</strong>
            {t("privacy.sections.infoWeCollect.cookiesNoteAfter", " see Section 5 below.")}
          </p>
        </>
      ),
    },
    {
      id: "how-we-use",
      navLabel: t("privacy.sections.howWeUse.navLabel", "How We Use Your Data"),
      title: t("privacy.sections.howWeUse.title", "3. How We Use Your Information"),
      body: (
        <ul>
          <li>{t("privacy.sections.howWeUse.listItem1", "To create and maintain your account and let you sign in securely.")}</li>
          <li>{t("privacy.sections.howWeUse.listItem2", "To deliver course content, track your progress, grade quizzes, and issue and verify certificates.")}</li>
          <li>{t("privacy.sections.howWeUse.listItem3", "To process event registrations and ticket payments, and to communicate essential details about events you register for.")}</li>
          <li>{t("privacy.sections.howWeUse.listItem4", "To personalize your experience, such as recommending relevant courses based on your activity.")}</li>
          <li>{t("privacy.sections.howWeUse.listItem5", "To send account-related communications: activation, password reset, enrollment confirmations, and in-app notifications about your learning activity, plus optional marketing communications you can opt out of at any time.")}</li>
          <li>
            {t("privacy.sections.howWeUse.listItem6Before", "To keep the Service secure, prevent fraud and abuse, and enforce our ")}
            <Link href={ROUTE.terms}>{t("privacy.sections.howWeUse.listItem6LinkText", "Terms of Use")}</Link>
            {t("privacy.sections.howWeUse.listItem6After", ".")}
          </li>
          <li>{t("privacy.sections.howWeUse.listItem7", "To analyze aggregate usage so we can improve courses and platform performance.")}</li>
        </ul>
      ),
    },
    {
      id: "legal-basis",
      navLabel: t("privacy.sections.legalBasis.navLabel", "Legal Basis (GDPR)"),
      title: t("privacy.sections.legalBasis.title", "4. Legal Basis for Processing"),
      body: (
        <>
          <p>{t("privacy.sections.legalBasis.intro", "If you are located in the European Economic Area or the UK, we rely on the following legal bases to process your personal data:")}</p>
          <ul>
            <li><strong>{t("privacy.sections.legalBasis.listItem1Label", "Performance of a contract")}</strong>{t("privacy.sections.legalBasis.listItem1Text", " — to create your account, deliver courses and certificates, and process event registrations you request.")}</li>
            <li><strong>{t("privacy.sections.legalBasis.listItem2Label", "Legitimate interests")}</strong>{t("privacy.sections.legalBasis.listItem2Text", " — to secure the Service, prevent fraud, and improve our courses and platform, balanced against your rights and interests.")}</li>
            <li><strong>{t("privacy.sections.legalBasis.listItem3Label", "Consent")}</strong>{t("privacy.sections.legalBasis.listItem3Text", " — for optional marketing communications and non-essential cookies, which you can withdraw at any time.")}</li>
            <li><strong>{t("privacy.sections.legalBasis.listItem4Label", "Legal obligation")}</strong>{t("privacy.sections.legalBasis.listItem4Text", " — where we must retain or disclose information to comply with the law.")}</li>
          </ul>
        </>
      ),
    },
    {
      id: "cookies",
      navLabel: t("privacy.sections.cookies.navLabel", "Cookies & Tracking"),
      title: t("privacy.sections.cookies.title", "5. Cookies and Tracking Technologies"),
      body: (
        <>
          <p>
            {t(
              "privacy.sections.cookies.para1",
              "We use a limited set of cookies and similar local-storage technologies of our own, summarized below. We do not use cookies to serve third-party advertising, and we do not build cross-site advertising profiles."
            )}
          </p>
          <CookieTable t={t} />
          <p>
            {t("privacy.sections.cookies.para2", "You can control non-essential cookies through the cookie banner shown on your first visit, and most browsers let you block or delete cookies through their settings. Blocking the strictly-necessary authentication cookie will prevent you from staying signed in.")}
          </p>

          <h3>{t("privacy.sections.cookies.subheading1", "Embedded course videos (YouTube)")}</h3>
          <p>
            {t(
              "privacy.sections.cookies.para3",
              "Course lesson videos are embedded YouTube videos, played through YouTube's own video player (see our Terms of Use for what this means for course content generally). When a lesson has a video, YouTube's player loads as soon as you open that lesson in the classroom — you do not need to press play first. That player — running as code served directly by YouTube, not by us — may set its own cookies or similar technologies and collect technical and usage data on YouTube/Google's own behalf as soon as it loads, independently of the cookies we set ourselves and listed in the table above. This can happen whether or not you have a YouTube or Google account."
            )}
          </p>
          <p>
            {t(
              "privacy.sections.cookies.para4Before",
              "We do not control, and cannot fully disclose on YouTube's behalf, what YouTube/Google does with this data — that is governed by "
            )}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
              {t("privacy.sections.cookies.para4LinkText", "Google's own Privacy Policy")}
            </a>
            {t(
              "privacy.sections.cookies.para4After",
              ", not this one. If you would prefer to avoid this, the only reliable way is to not open lessons that have a video attached; most browsers also let you block third-party cookies globally in your settings, though this may affect video playback."
            )}
          </p>
        </>
      ),
    },
    {
      id: "sharing",
      navLabel: t("privacy.sections.sharing.navLabel", "How We Share Information"),
      title: t("privacy.sections.sharing.title", "6. Third-Party Service Providers and Data Sharing"),
      body: (
        <>
          <p>
            {t("privacy.sections.sharing.para1", "We do not sell your personal information. We share information only in the following limited circumstances:")}
          </p>
          <ul>
            <li><strong>{t("privacy.sections.sharing.listItem1Label", "Service providers (processors)")}</strong>{t("privacy.sections.sharing.listItem1Text", " who perform functions on our behalf under contractual confidentiality and data-protection obligations — for example, reputable third-party providers for email delivery, payment processing for event tickets, analytics, and cloud hosting/infrastructure.")}</li>
            <li><strong>{t("privacy.sections.sharing.listItem2Label", "Legal reasons")}</strong>{t("privacy.sections.sharing.listItem2Text", ", where required to comply with a law, regulation, subpoena, or valid legal process, or to protect the rights, property, or safety of {{appName}}, our users, or the public.", { appName })}</li>
            <li><strong>{t("privacy.sections.sharing.listItem3Label", "Business transfers")}</strong>{t("privacy.sections.sharing.listItem3Text", ", if we are involved in a merger, acquisition, financing, or sale of assets, in which case we will notify you before your personal data becomes subject to a different privacy policy.")}</li>
            <li><strong>{t("privacy.sections.sharing.listItem4Label", "With your direction")}</strong>{t("privacy.sections.sharing.listItem4Text", ", such as when you choose to make parts of your profile or certificate visible to other users or the public.")}</li>
            <li><strong>{t("privacy.sections.sharing.listItem5Label", "Event organizers")}</strong>{t("privacy.sections.sharing.listItem5Text", ", limited to the name, email, and phone number submitted for that specific reservation, so the organizer of the event you registered for can manage attendance (for example, checking you in, or contacting you about a schedule change) — an organizer never receives your other account details.")}</li>
          </ul>
          <h3>{t("privacy.sections.sharing.subheading1", "Internal access to notes and reviews")}</h3>
          <p>
            {t("privacy.sections.sharing.para2Before", "Your private lesson notes are not reviewed by our staff as a matter of course. Course reviews and ratings you post publicly may be reviewed by authorized administrators to enforce our ")}
            <Link href={ROUTE.terms}>{t("privacy.sections.sharing.para2LinkText", "Terms of Use")}</Link>
            {t(
              "privacy.sections.sharing.para2After",
              " (for example, removing a review that contains harassment or unlawful content). A private note's content is accessed only when it has been specifically reported or flagged for a documented reason (such as a policy or legal concern) and an authorized administrator opens it to investigate — an action that requires a stated reason and is logged, including who accessed it and when. Outside of a flagged note, our staff see only metadata about your notes (such as which lesson a note relates to and its length), never the content."
            )}
          </p>
        </>
      ),
    },
    {
      id: "retention",
      navLabel: t("privacy.sections.retention.navLabel", "Data Retention"),
      title: t("privacy.sections.retention.title", "7. Data Retention"),
      body: (
        <p>
          {t(
            "privacy.sections.retention.para1",
            "We retain your account and learning data for as long as your account is active, so you keep access to your progress and certificates, and for a reasonable period afterward to comply with legal, tax, security, or dispute-resolution obligations. If you delete your account — whether through the self-service option in your dashboard settings or by contacting us — we deactivate it and sign you out of every device immediately, and we delete or anonymize your personal information within a reasonable period, except for records we are required to retain by law (such as transaction records for paid tickets) or that are necessary to resolve disputes or enforce our agreements. Two-factor authentication codes are retained only for the few minutes needed to complete your login and are never kept once used or expired."
          )}
        </p>
      ),
    },
    {
      id: "your-rights",
      navLabel: t("privacy.sections.yourRights.navLabel", "Your Privacy Rights"),
      title: t("privacy.sections.yourRights.title", "8. Your Rights and Choices"),
      body: (
        <>
          <p>
            {t("privacy.sections.yourRights.intro", "Regardless of where you live, we offer you the following rights over your personal data, consistent with frameworks such as the GDPR and CCPA:")}
          </p>
          <ul>
            <li><strong>{t("privacy.sections.yourRights.listItem1Label", "Access")}</strong>{t("privacy.sections.yourRights.listItem1Text", " — request a copy of the personal data we hold about you.")}</li>
            <li><strong>{t("privacy.sections.yourRights.listItem2Label", "Correction")}</strong>{t("privacy.sections.yourRights.listItem2Text", " — correct inaccurate or incomplete data, most of which you can update directly from your dashboard settings.")}</li>
            <li><strong>{t("privacy.sections.yourRights.listItem3Label", "Deletion / erasure")}</strong>{t("privacy.sections.yourRights.listItem3Text", " — delete your account yourself at any time from your dashboard settings (Danger Zone), or request that we do it for you, subject to the retention exceptions in Section 7.")}</li>
            <li><strong>{t("privacy.sections.yourRights.listItem4Label", "Portability")}</strong>{t("privacy.sections.yourRights.listItem4Text", " — request an export of certain data you provided to us, in a structured, commonly used format.")}</li>
            <li><strong>{t("privacy.sections.yourRights.listItem5Label", "Objection and restriction")}</strong>{t("privacy.sections.yourRights.listItem5Text", " — object to, or ask us to restrict, certain processing based on legitimate interests.")}</li>
            <li><strong>{t("privacy.sections.yourRights.listItem6Label", "Opt-out of marketing")}</strong>{t("privacy.sections.yourRights.listItem6Text", " — unsubscribe from non-essential marketing emails at any time using the link in those emails; account-security emails (like password resets) cannot be turned off.")}</li>
            <li><strong>{t("privacy.sections.yourRights.listItem7Label", "Non-discrimination")}</strong>{t("privacy.sections.yourRights.listItem7Text", " — we will not deny you service or otherwise discriminate against you for exercising any of these rights.")}</li>
          </ul>
          <p>
            {t("privacy.sections.yourRights.para1Before", "To exercise any of these rights, contact us at ")}
            <Link href={`mailto:${CONFIG.EMAIL_1}`}>{CONFIG.EMAIL_1}</Link>
            {t("privacy.sections.yourRights.para1After", ". We may need to verify your identity before completing certain requests, and we will respond within the timeframe required by applicable law.")}
          </p>
        </>
      ),
    },
    {
      id: "children",
      navLabel: t("privacy.sections.children.navLabel", "Children's Privacy"),
      title: t("privacy.sections.children.title", "9. Children's Privacy"),
      body: (
        <p>
          {t(
            "privacy.sections.children.para1Before",
            "The Service is not directed at, and is not intended for use by, children under 16. We do not knowingly collect personal information from children under 16. If you believe a child has provided us with personal information without appropriate parental or guardian consent, please contact us at "
          )}
          <Link href={`mailto:${CONFIG.EMAIL_1}`}>{CONFIG.EMAIL_1}</Link>
          {t("privacy.sections.children.para1After", " so we can investigate and remove it as appropriate.")}
        </p>
      ),
    },
    {
      id: "international-transfers",
      navLabel: t("privacy.sections.internationalTransfers.navLabel", "International Transfers"),
      title: t("privacy.sections.internationalTransfers.title", "10. International Data Transfers"),
      body: (
        <p>
          {t(
            "privacy.sections.internationalTransfers.para1",
            "{{appName}} serves learners around the world, which means your information may be transferred to, stored, and processed in a country other than the one in which you live, including countries that may have different data-protection laws than your own. Where we transfer personal data internationally, we use appropriate safeguards recognized under applicable law, such as standard contractual clauses, to help ensure your information continues to receive an adequate level of protection.",
            { appName }
          )}
        </p>
      ),
    },
    {
      id: "security",
      navLabel: t("privacy.sections.security.navLabel", "Data Security"),
      title: t("privacy.sections.security.title", "11. Data Security"),
      body: (
        <p>
          {t(
            "privacy.sections.security.para1",
            "We use reasonable technical and organizational measures designed to protect your information, including encryption of data in transit (HTTPS/TLS), encrypted password storage, role-based access controls limiting who can view your data internally, and routine review of our security practices. No method of transmission or storage is 100% secure, and we cannot guarantee absolute security, but we work to continuously improve our safeguards and will notify affected users and relevant authorities of any data breach as required by applicable law."
          )}
        </p>
      ),
    },
    {
      id: "tutor-applications",
      navLabel: t("privacy.sections.tutorApplications.navLabel", "Tutor Applications"),
      title: t("privacy.sections.tutorApplications.title", "12. Tutor Applications"),
      body: (
        <>
          <p>
            {t(
              "privacy.sections.tutorApplications.para1",
              "If you submit our \"Apply to teach\" form to propose a course or other content as a tutor, we collect the information you provide in that form: your name, email address, WhatsApp number, the course title you'd like to add, a link to your YouTube channel, your Instagram and/or Twitter/X handles if you choose to share them, and whatever you write in the free-text \"Tell us about yourself\" field."
            )}
          </p>
          <p>
            {t(
              "privacy.sections.tutorApplications.para2",
              "We use this information solely to review your application, assess the proposed course for quality and fit, and contact you about it — including by email or WhatsApp. We do not use it for marketing, and we do not publish it. If your application is accepted and you go on to create a published Contributed Course, see Section 12 of our Terms of Use for how that content itself is handled."
            )}
          </p>
          <p>
            {t(
              "privacy.sections.tutorApplications.para3",
              "If your application is not accepted, or you decide not to proceed, we retain the submitted information for a reasonable period for our internal records before deleting it, unless you ask us to delete it sooner by contacting us using the details in Section 14."
            )}
          </p>
        </>
      ),
    },
    {
      id: "changes",
      navLabel: t("privacy.sections.changes.navLabel", "Changes to This Policy"),
      title: t("privacy.sections.changes.title", "13. Changes to This Policy"),
      body: (
        <p>
          {t(
            "privacy.sections.changes.para1",
            "We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. If we make material changes, we will notify you by email, by an in-app notification, or by posting a notice on the Service before the changes take effect. The “Last updated” date at the top of this page always reflects the most recent version."
          )}
        </p>
      ),
    },
    {
      id: "contact",
      navLabel: t("privacy.sections.contact.navLabel", "Contact Us"),
      title: t("privacy.sections.contact.title", "13. Contact Us"),
      body: (
        <p>
          {t("privacy.sections.contact.para1Before", "Questions about this Privacy Policy, or requests relating to your personal data, should be sent to ")}
          <Link href={`mailto:${CONFIG.EMAIL_1}`}>{CONFIG.EMAIL_1}</Link>
          {t("privacy.sections.contact.para1Middle", ", or via our ")}
          <Link href={ROUTE.contact ?? "/contact"}>{t("privacy.sections.contact.para1LinkText", "Contact page")}</Link>
          {t("privacy.sections.contact.para1After", ".")}
        </p>
      ),
    },
  ];
};

const PrivacyPolicy = () => {
  const { t } = useTranslation("legal");
  const sections = useSections(t);

  return (
    <main className="rbt-main-wrapper">
      <LegalPageLayout
        eyebrow={t("privacy.eyebrow", "Legal")}
        title={t("privacy.title", "Privacy Policy")}
        description={t(
          "privacy.description",
          "How SmartTech Academy collects, uses, and protects your information — and the rights you have over your own data, wherever you are."
        )}
        lastUpdated={LAST_UPDATED}
        sections={sections}
      />

      <CallToActionOne />

      <div
        className="rbt-newsletter-area bg-color-primary newsletter-style-2 rbt-section-gap"
        style={{ background: "url(" + image_url("../app_images/bg/dots.png") + ") repeat center/auto" }}
      >
        <Newsletter />
      </div>
    </main>
  );
};

export default PrivacyPolicy;
